"use client";

import { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Testimonios() {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top;
        setScrollY(sectionTop);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const parallaxOffset = scrollY * -0.15;
  const testimonials = [{
    id: 1,
    quote: "La atención personalizada y el ambiente acogedor hacen de este lugar mi refugio favorito. Cada visita es una experiencia única.",
    author: "María González",
    role: "Cliente Regular"
  }, {
    id: 2,
    quote: "El equilibrio perfecto entre tradición y modernidad. Los detalles artesanales y el servicio excepcional superan todas las expectativas.",
    author: "Carlos Ramírez",
    role: "Empresario"
  }, {
    id: 3,
    quote: "Un espacio donde cada elemento cuenta una historia. La calidad y autenticidad se perciben en cada rincón.",
    author: "Ana Martínez",
    role: "Diseñadora"
  }, {
    id: 4,
    quote: "Desde el primer momento sentí la calidez humana y profesionalismo. Es más que un servicio, es una experiencia completa.",
    author: "Roberto Silva",
    role: "Arquitecto"
  }, {
    id: 5,
    quote: "La combinación de elementos naturales y el cuidado en cada detalle crean una atmósfera inigualable. Altamente recomendado.",
    author: "Laura Fernández",
    role: "Consultora"
  }, {
    id: 6,
    quote: "Un verdadero tesoro que refleja pasión y dedicación. La experiencia va más allá de lo esperado, dejando una impresión duradera.",
    author: "Diego Morales",
    role: "Chef"
  }];
  return <section id="testimonios" ref={sectionRef} className="relative bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      transform: `translateY(${parallaxOffset}px)`
    }}>
        <svg className="absolute top-20 right-10 w-64 h-64" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 100 L120 60 L115 55 L105 65 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L80 60 L85 55 L95 65 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L140 80 L145 75 L125 85 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L60 80 L55 75 L75 85 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L130 120 L135 125 L115 115 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L70 120 L65 125 L85 115 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <circle cx="100" cy="100" r="8" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
        </svg>

        <svg className="absolute bottom-32 left-16 w-48 h-48 rotate-45" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 100 L120 60 L115 55 L105 65 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L80 60 L85 55 L95 65 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L140 80 L145 75 L125 85 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <path d="M100 100 L60 80 L55 75 L75 85 Z" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
          <circle cx="100" cy="100" r="8" stroke="currentColor" strokeWidth="1" fill="none" className="color-accent" />
        </svg>
      </div>

      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        <div className="flex flex-col gap-8">
          {testimonials.map((testimonial, i) => <TestimonialCard key={testimonial.id} testimonial={testimonial} index={i} />)}
        </div>
      </div>
    </section>;
}
function TestimonialCard({
  testimonial,
  index
}: {
  testimonial: {
    id: number;
    quote: string;
    author: string;
    role: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-[600ms] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="bg-muted border-none shadow-[0_6px_16px_rgba(0,0,0,0.08)] rounded-lg hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all duration-[var(--duration-medium)]">
        <CardContent className="p-8 relative">
          <Quote className="absolute top-6 left-6 w-8 h-8 color-accent opacity-30" />
          
          <blockquote className="font-display italic text-lg leading-relaxed color-foreground mt-8 mb-6">
            {testimonial.quote}
          </blockquote>
          
          <div className="flex flex-col gap-1">
            <cite className="not-italic font-semibold color-foreground text-base">
              {testimonial.author}
            </cite>
            <span className="text-sm color-muted-foreground">
              {testimonial.role}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>;
}